import type { RequestInfo } from '@warp-drive/core-types/request';
import { buildBaseURL } from '@warp-drive/utilities';

/**
 * GET /me — the session-bootstrap document (current user + the sideloaded
 * relationships the consuming app needs). WarpDrive migration Phase 2.5:
 * replaces the raw fetch + `pushPayload` path in each app's bootstrap;
 * flowing through `store.request` gives it the uniform header bundle (auth
 * token, Time-Zone, X-Request-Id, x-api-key), response/auth handling, and
 * cache materialization for free.
 *
 * The `/me` *include* set is app-specific — tio-employee and tio-agent
 * bootstrap different relationship graphs — so callers pass their own.
 * Accepts either a pre-joined string or an array of include paths (joined
 * with commas).
 *
 * Always network-fresh (`cacheOptions.reload`): consumers re-run this to pick
 * up server-side session changes (accepting terms, role grants), and a
 * cache-served document silently leaves the session on the stale pre-write
 * state — the gates that read it then never flip.
 */
export function meRequest(includes: string | readonly string[]): RequestInfo {
  const base = buildBaseURL({
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'me',
  });
  const include = Array.isArray(includes) ? includes.join(',') : includes;
  return {
    url: `${base}?include=${include}`,
    method: 'GET',
    op: 'query',
    cacheOptions: { reload: true },
  };
}
